import { inject, Injectable } from '@angular/core';
import { ClusteringRequest } from '@core/api/naviqore-clustering-data-api.service';
import { ExportGraphRequest } from '@core/api/naviqore-data-api.service';
import {
  DateToItsTimezone,
  TimeZoneFormat,
} from '@core/constants/time.defaults';
import padWithZeros from '@core/utils/padWithZeros';
import {
  ToDateSelector,
  SelectedVesselImo,
  SetSelectedVesselAction,
  SetVariablesDataAction,
  SetSystemDatesAction,
  FromDateSelector,
  SetTimezoneAction,
  TimezoneSelector,
  VesselSidebarApiActions,
  VesselInfoWindowApiActions,
  SetAggregation,
  AggregationSelector,
  SetChartErrorMessage,
  ClearChartErrorMessage,
  LoadExtraVesselInfoDataAction,
} from '@dashboard-store';
import { ApiEffectsHelperService } from '@dashboard/dashboard-store/effects/api-effects-helper.service';
import { ExtraStateIds } from '@dashboard/models';
import {
  DataSeriesVariable,
  DataToApiService,
} from '@dashboard/services/data-to-api.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import calcAggr from '@shared/utils/calcAggregation';
import {
  catchError,
  debounceTime,
  EMPTY,
  of,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { RouteIds, RouteStatics } from '@dashboard/dynamic-dashboards.route';
import { DebounceHackMs } from '@dashboard/dashboard-store/effects/api-debounce-hack';
@Injectable()
export class ApiEffects {
  private _dataToApi = inject(DataToApiService);
  private actions$ = inject(Actions);
  private _store = inject(Store);
  private _apiEffectHelper = inject(ApiEffectsHelperService);
  private _router = inject(Router);

  ApplyTimezone$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SetTimezoneAction),
      withLatestFrom(
        this._store.select(ToDateSelector),
        this._store.select(FromDateSelector)
      ),
      switchMap(([{ timezone }, _toDate, _fromDate]) => {
        const toDate = DateToItsTimezone(_toDate, timezone);
        const fromDate = DateToItsTimezone(_fromDate, timezone);

        return of(SetSystemDatesAction({ toDate, fromDate }));
      })
    );
  });

  //This effect works when we open the first sidebar
  loadSidebarData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SetSelectedVesselAction, SetSystemDatesAction, SetAggregation),
      debounceTime(DebounceHackMs),
      withLatestFrom(
        this._store.select(ToDateSelector),
        this._store.select(FromDateSelector),
        this._store.select(SelectedVesselImo),
        this._store.select(AggregationSelector),
        this._store.select(TimezoneSelector)
      ),

      switchMap(
        ([, toDate, fromDate, selectedImo, aggregation, timezoneHours]) => {
          if (!this.isVesselRouteActive()) return EMPTY;

          this._store.dispatch(VesselSidebarApiActions.call());
          const queryDataVars: DataSeriesVariable[] = [];
          const clusteringVars: DataSeriesVariable[] = [];

          if (!selectedImo) return EMPTY;

          if (selectedImo) {
            const sidebardData = this._dataToApi.SidebarQueryPayload();
            queryDataVars.push(...sidebardData);
          }

          const queryDataPayload: ExportGraphRequest = {
            start: fromDate,
            end: toDate,
            variables: queryDataVars.map((v) => ({ tagId: Number(v.tagId) })),
            filters: [],
          };

          const clusteringPayload: ClusteringRequest = {
            start: fromDate,
            end: toDate,
            variables: clusteringVars.map((v) => {
              let obj = { tagId: Number(v.tagId) };

              if (v.extraPayloadProps) {
                obj = { ...obj, ...v.extraPayloadProps };
              }
              return obj;
            }),
            filters: [],
          };

          if (aggregation) {
            const _aggregation = calcAggr(aggregation.size, aggregation.step);

            queryDataPayload['aggregation'] = _aggregation;

            clusteringPayload['aggregation'] = 1;
          }

          return this._apiEffectHelper
            .ApiDataRequestsToState(
              selectedImo,
              toDate,
              { queryDataPayload, clusteringPayload },
              { queryDataVars, clusteringVars, timezoneHours }
            )
            .pipe(
              switchMap(([dataResult, clusteringResult]) => {
                const actions: Action[] = [VesselSidebarApiActions.success()];

                if (dataResult.ok === true) {
                  actions.push(
                    SetVariablesDataAction({
                      variableMeta: dataResult.data,
                      source: 'API',
                    }),
                    ClearChartErrorMessage({ key: 'queryData' })
                  );
                } else {
                  actions.push(
                    SetChartErrorMessage({
                      key: 'queryData', // you choose the key
                      errorMsg: 'Error getting query data',
                    }),
                    ActionToEmptySeries(
                      dataResult.error.vars,
                      selectedImo,
                      toDate
                    )
                  );
                }
                if (clusteringResult.ok == true) {
                  actions.push(
                    SetVariablesDataAction({
                      variableMeta: clusteringResult.data,
                      source: 'API',
                    })
                  );
                  actions.push(ClearChartErrorMessage({ key: 'clustering' }));
                } else {
                  actions.push(
                    SetChartErrorMessage({
                      key: 'clustering',
                      errorMsg: clusteringResult.error.message,
                    })
                  );
                }
                return of(...actions);
              }),
              catchError((error) => {
                console.error('Error in API request:', error);
                return of(VesselSidebarApiActions.failure());
              })
            );
        }
      )
    );
  });
  //This effect works when we open the big dashboard after we have open the sidebar
  loadVesselInfoData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        SetSelectedVesselAction,
        SetSystemDatesAction,
        LoadExtraVesselInfoDataAction,
        SetAggregation
      ),
      debounceTime(DebounceHackMs),
      withLatestFrom(
        this._store.select(ToDateSelector),
        this._store.select(FromDateSelector),
        this._store.select(SelectedVesselImo),
        this._store.select(AggregationSelector),
        this._store.select(TimezoneSelector)
      ),

      switchMap(
        ([, toDate, fromDate, selectedImo, aggregation, timezoneHours]) => {
          if (!this.isInfoPageActive()) return EMPTY;

          const queryDataVars: DataSeriesVariable[] = [];
          const clusteringVars: DataSeriesVariable[] = [];

          this._store.dispatch(VesselInfoWindowApiActions.call());

          if (!selectedImo) return EMPTY;

          const windoInfoData = this._dataToApi.WindowInfoDataQueryPayload();
          const clusteringData =
            this._dataToApi.WindowInfoClusteringQueryPayload();
          clusteringVars.push(...clusteringData);
          queryDataVars.push(...windoInfoData);

          const queryDataPayload: ExportGraphRequest = {
            start: fromDate,
            end: toDate,
            variables: queryDataVars.map((v) => ({ tagId: Number(v.tagId) })),
            filters: [],
          };

          const clusteringPayload: ClusteringRequest = {
            start: fromDate,
            end: toDate,
            variables: clusteringVars.map((v) => {
              let obj = { tagId: Number(v.tagId) };

              if (v.extraPayloadProps) {
                obj = { ...obj, ...v.extraPayloadProps };
              }
              return obj;
            }),
            filters: [],
          };

          if (aggregation) {
            const _aggregation = calcAggr(aggregation.size, aggregation.step);

            queryDataPayload['aggregation'] = _aggregation;

            clusteringPayload['aggregation'] = 1;
          }

          return this._apiEffectHelper
            .ApiDataRequestsToState(
              selectedImo,
              toDate,
              { queryDataPayload, clusteringPayload },
              { queryDataVars, clusteringVars, timezoneHours }
            )
            .pipe(
              switchMap(([dataResult, clusteringResult]) => {
                console.log({ dataResult, clusteringResult });
                const actions: Action[] = [
                  VesselInfoWindowApiActions.success(),
                ];

                //we make the equality here to satisfy the type cast. if we remove it typescript dont know if there is ok or error property
                if (dataResult.ok === true) {
                  actions.push(
                    SetVariablesDataAction({
                      variableMeta: dataResult.data,
                      source: 'API',
                    }),
                    ClearChartErrorMessage({ key: 'queryData' })
                  );
                } else {
                  actions.push(
                    SetChartErrorMessage({
                      key: 'queryData', // you choose the key
                      errorMsg: 'Error getting query data',
                    }),
                    ActionToEmptySeries(
                      dataResult.error.vars,
                      selectedImo,
                      toDate
                    )
                  );
                }
                //we make the equality here to satisfy the type cast. if we remove it typescript dont know if there is ok or error property
                if (clusteringResult.ok === true) {
                  actions.push(
                    SetVariablesDataAction({
                      variableMeta: clusteringResult.data,
                      source: 'API',
                    }),
                    ClearChartErrorMessage({ key: 'clustering' })
                  );
                } else {
                  actions.push(
                    SetChartErrorMessage({
                      key: 'clustering',
                      errorMsg: clusteringResult.error.message,
                    }),
                    ActionToEmptySeries(
                      clusteringResult.error.vars,
                      selectedImo,
                      toDate
                    )
                  );
                }

                console.log({ actions });
                return of(...actions);
              }),
              catchError((error) => {
                console.error('Error in API request:', error);
                return of(VesselInfoWindowApiActions.failure());
              })
            );
        }
      )
    );
  });

  private isVesselRouteActive(): boolean {
    let route: ActivatedRouteSnapshot | null =
      this._router.routerState.snapshot.root;
    while (route) {
      if (route.routeConfig?.path === `:${RouteIds.vesselImo}`) return true;
      route = route.firstChild;
    }
    return false;
  }

  private isInfoPageActive(): boolean {
    let route: ActivatedRouteSnapshot | null =
      this._router.routerState.snapshot.root;
    while (route) {
      if (route.routeConfig?.path === RouteStatics.infoPageWithTab) return true;
      route = route.firstChild;
    }
    return false;
  }
}

function ActionToEmptySeries(
  series: ExtraStateIds[],
  selectedImo: string,
  toDate: string
) {
  return SetVariablesDataAction({
    variableMeta: series.map((series) => [
      padWithZeros(selectedImo, 8),
      series as ExtraStateIds,
      {
        formattedValue: null,
        timestamp: TimeZoneFormat(toDate).valueOf(),
        variableId: 'removethis',
        notInSync: false,
        values: [],
      },
    ]),
    source: 'API',
  });
}
